import { RestManager } from '@/newApi/services/rest/RestManager';
import { getApiHost } from '@/appSettings';
import type { EmploymentTableRow, ReviewDraft } from '@sysarb/sysarb-types-v2/services/v1/pay-management';
import type { useResgate } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';

const payManagementService = new RestManager(`https://${getApiHost()}/pay-management-service`);

export function abort(): void {
    payManagementService.abort();
}

/**
 * Update Review draft.
 * @param reviewDraftId Review draft id
 * @param reviewDraft Review draft to update
 */
export function updateReviewDraft(resgate: ReturnType<typeof useResgate>, reviewDraftId: string, reviewDraft: ReviewDraft): void {
    const payload = {
        name: reviewDraft.name,
        dueDate: reviewDraft.dueDate,
        description: reviewDraft.description,
        minimumSalaryAppliedTo: reviewDraft.minimumSalaryAppliedTo,
        attestManager: reviewDraft.attestManager,
        attestSectionManager: reviewDraft.attestSectionManager,
        attestEmployee: reviewDraft.attestEmployee,
        attestUnion: reviewDraft.attestUnion,
        acceptLowestAssessment: reviewDraft.acceptLowestAssessment,
        workHoursPerMonth: reviewDraft.workHoursPerMonth,
        currencyGroupID: reviewDraft.currencyGroupID,
        filter: reviewDraft.filter
    };
    void resgate.call(`v1.pay-management.review-draft.${reviewDraftId}`, 'update', payload);
}

/**
 * Creates a review draft
 * @returns Promise<ReviewDraft>
 */
export async function createReviewDraft(resgate: ReturnType<typeof useResgate>): Promise<ReviewDraft | null> {
    const promise = resgate.call<ReviewDraft>('v1.pay-management.review-draft', 'create', {}).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Delete Pay management review draft by id
 * @param reviewDraftId Pay management review draft id
 */
export async function deleteReviewDraft(resgate: ReturnType<typeof useResgate>, reviewDraftId: string): Promise<ReviewDraft | null> {
    const promise = resgate.call<ReviewDraft>(`v1.pay-management.review-draft.${reviewDraftId}`, 'delete', {}).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get review draft by id
 * @param reviewDraftId Pay management id
 * @param componentId Id of component calling the function
 */
export async function getReviewDraftById(resgate: ReturnType<typeof useResgate>, reviewDraftId: string): Promise<ReviewDraft | null> {
    const promise = resgate.model<ReviewDraft>(`v1.pay-management.review-draft.${reviewDraftId}`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetches all Pay managament review drafts
 * @param componentId Id of component calling the function
 * @returns Promise<(Iterable<ReviewDraft>) | null>
 */
export async function getAllReviewDrafts(resgate: ReturnType<typeof useResgate>): Promise<Iterable<ReviewDraft> | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.collection<ReviewDraft>(`v1.pay-management.review-draft.${instanceTopic ?? ''}.getbysubdomain`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetch all employments from a reviewDraft id.
 * @param reviewDraftId Id of the pay management review
 * @returns employments filtered on the pay management review
 */
export async function getEmployments(reviewDraftId: string): Promise<EmploymentTableRow[]> {
    const { instance } = useLoginStore();
    return payManagementService.post<EmploymentTableRow[]>({}, `v1/${instance}/pay-management/employments/${reviewDraftId}/gettabledata`);
}
